import { Tabs } from '@chakra-ui/react';
import { forwardRef } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { MLink } from '../chakra';
import './styles.scss';

export interface MCustomLinkTabProps extends LinkProps {
  /** Injected positionally by the parent `MTabList` — not usually set directly. */
  value?: string;
}

/*
 * A tab that navigates via react-router instead of switching an internal
 * panel — used for route-based nav bars styled as tabs (`MTabs` renders
 * purely for the visual selected/hover/focus states; the actual page
 * content underneath is a sibling `<Routes>`, not `MTabPanels`).
 *
 * v2's version used `useTab()` + `useMultiStyleConfig('Tabs', tabProps)` —
 * both fully removed in v3, no direct replacement. Rebuilt on `asChild`
 * instead: `Tabs.Trigger` owns the real `role="tab"`/`aria-selected`/
 * `data-selected`/keyboard-nav behavior and merges its props onto the
 * `Link` it wraps, so the design-system Tabs recipe's `_selected`/`_hover`
 * styles apply to this exactly like a real in-page tab. `Tabs.Root`'s
 * `value` should be kept in sync with the current route by the caller
 * (e.g. deriving it from the matched route on every render) — clicking
 * navigates via `Link`'s own handler, which changes the URL and lets that
 * external sync take over; no `onValueChange` round-trip is needed.
 *
 * Pass `navigate={null}` to that `MTabs`. Zag's Tabs calls `navigate`
 * whenever the selected value changes while the selected trigger is an
 * anchor — here, *after* the `Link` has already navigated, because the value
 * follows the route. A second navigation from there pushed a duplicate
 * history entry per click (so Back appeared to do nothing), and any route
 * change into a nested path under another tab (e.g. an account's
 * `contacts/create`) got bounced to that tab's root. v2 never navigated on
 * selection; the `Link` was the only navigation. Omitting `navigate` isn't
 * equivalent: zag's default re-clicks the anchor, another navigation.
 */
export const MCustomLinkTab = forwardRef<
  HTMLAnchorElement,
  MCustomLinkTabProps
>(({ value, children, to, ...rest }, ref) => (
  <Tabs.Trigger value={value ?? ''} asChild>
    {/* `unstyled` — the Tabs recipe owns this trigger's color/hover/focus
     * states; Link's own recipe (blue text, underline-on-hover, its own
     * focus ring) would otherwise compete with it on the same element. */}
    <MLink asChild unstyled>
      {/* `replace` avoids piling up history entries when switching between
       * these route-style tabs; kept after `rest` so it can't be overridden. */}
      <Link ref={ref} to={to} {...rest} replace>
        {/* The built-in trigger recipe makes this anchor `display: flex`.
         * `boldContentHidden`'s reserved-width `::after` needs to be a
         * block-level child of an inline/shrink-to-fit box to stack under
         * the label and reserve its max width — nested here inside a span,
         * it does. Directly on the flex anchor, it instead becomes a
         * second flex row item, roughly doubling the trigger's width. */}
        <span data-content={children} className="boldContentHidden">
          {children}
        </span>
      </Link>
    </MLink>
  </Tabs.Trigger>
));

MCustomLinkTab.displayName = 'MCustomLinkTab';

export default MCustomLinkTab;
